import react from 'react';
import styled, { css } from 'styled-components';

import { hourFormat, shortDateFormat } from '@paradigm/utils/src/date';
import { formatProductQty } from '@paradigm/features/src/unified-rfqs/ui/decimal-format';
import { isDatedRfqLegs } from '@paradigm/features/src/unified-rfqs/ui/rfqs/legs-functions';
import {
  lima,
  paradigmLightBlue,
  razzmatazz,
} from '@paradigm/design-system/src/colors';

import VirtualizedRow from '#/components/rfq-common/TableRow';
import {
  findBaseProductCode,
  getProductName,
} from '@paradigm/features/src/products/domain';
import { Order } from '@paradigm/features/src/unified-rfqs/entities/shared';
import { GrfqQuote } from '@paradigm/features/src/unified-rfqs/entities/grfq/domain';
import { Order as DrfqOrder } from '@paradigm/features/src/unified-rfqs/entities/drfq/orders';
import { useOrder } from '@paradigm/features/src/unified-rfqs/contexts/unified-orders';
import useFetchedDrfq from '@paradigm/features/src/unified-rfqs/contexts/use-fetched-drfq';
import { convertToBigNumber } from '@paradigm/utils/formatDecimal';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { useContextMenuActions } from '#/components/UnifiedRfqs/context-menu/context-menu';
import { StrategyDescription } from '#/components/UnifiedRfqs/components/strategy-description/StrategyDescription';
import { LongText } from '#/components/UnifiedRfqs/components/LongText';
import {
  ActionsContainer,
  QuickCreateButton,
} from '#/components/UnifiedRfqs/components/QuickActions';
import BottomTableRow, {
  DesktopRow,
} from '#/components/UnifiedRfqs/BottomTableRow';
import KillQuoteButton from '#/components/UnifiedRfqs/open-orders/KillQuoteButton';
import KillOrderButton from '#/components/UnifiedRfqs/open-orders/KillOrderButton';
import makeColumns from '#/components/UnifiedRfqs/open-orders/makeColumns';
import { RfqProvider } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import useFormattedPrice, {
  useFormattedGrfqQuotePrice,
} from '@paradigm/features/src/unified-rfqs/ui/hooks/useFormattedPrice';

interface Props {
  readonly orderId: Order['id'];
  readonly scrollableRef: react.RefObject<HTMLDivElement>;
}

export interface ObContextMenuData {
  readonly quote: GrfqQuote;
  readonly auctionType: 'OB';
}

export interface RfqContextMenuData {
  readonly order: DrfqOrder;
  readonly auctionType: 'RFQ';
}

const baseDDTag = 'Open Order Quick Actions';

export default function OrderRow(props: Props) {
  const { scrollableRef } = props;
  const { orderId } = props;

  return (
    <VirtualizedRow
      id={orderId}
      scrollableRef={scrollableRef}
      rowContent={() => <OrderRowContent orderId={orderId} />}
    />
  );
}

function RfqOrderRow({ order }: { order: Order }) {
  switch (order.rfqType) {
    case 'grfq':
      return <GrfqOrderRow quote={order} />;
    case 'drfq':
      return <DrfqOrderRow order={order} />;
    default:
      throw new TypeError('Unexpected open order row with invalid rfq type.');
  }
}

function OrderRowContent({ orderId }: { orderId: Order['id'] }) {
  const order = useOrder(orderId);
  return (
    <RfqProvider rfqId={order.rfq_id}>
      <RfqOrderRow order={order} />
    </RfqProvider>
  );
}

function DrfqOrderRow({ order }: { order: DrfqOrder }) {
  const { drfq } = useFetchedDrfq(order.rfq_id);
  const { open } = useContextMenuActions();
  const productCode = findBaseProductCode(drfq?.product_codes ?? []);
  const isDated = isDatedRfqLegs(drfq?.legs ?? [], productCode);
  const qtyFormatter = (value: string | number) =>
    formatProductQty(value, productCode, true, isDated);
  const filled = convertToBigNumber(order.filled_quantity);
  const cancelled = convertToBigNumber(order.canceled_quantity);
  const orderQuantity = convertToBigNumber(order.quantity);
  const remainingQty = orderQuantity.minus(filled).minus(cancelled).toString();
  const formattedPrice = useFormattedPrice({
    price: order.price,
    side: order.side,
    rfqType: 'drfq',
    productCode,
    description: drfq?.description,
  });

  const description = (
    <StrategyDescription>{drfq?.description ?? '-'}</StrategyDescription>
  );

  const actions = (
    <ActionsContainer>
      <QuickCreateButton
        baseDDTag={baseDDTag}
        type="RFQ"
        rfqId={order.rfq_id}
        productCode={productCode}
        prefillAccount={order.account_name}
      />
      <QuickCreateButton
        baseDDTag={baseDDTag}
        type="OB"
        rfqId={order.rfq_id}
        productCode={productCode}
      />
      <KillOrderButton orderId={order.id} />
    </ActionsContainer>
  );

  const orderSide = <ActionLabel>{order.side}</ActionLabel>;

  const quote = (
    <QuoteContainer>
      <QuoteRow>
        <QuoteCell>{qtyFormatter(order.quantity)}</QuoteCell>
        <QuoteCell>{formattedPrice}</QuoteCell>
        {orderSide}
      </QuoteRow>
    </QuoteContainer>
  );

  const oid = <LongText tooltip={order.id}>{order.id}</LongText>;

  const account = (
    <LongText tooltip={order.account_name}>{order.account_name}</LongText>
  );

  const columns = makeColumns({
    account,
    actions,
    auction: 'RFQ',
    completed: qtyFormatter(order.filled_quantity),
    date: shortDateFormat(order.last_updated_at),
    description,
    oid,
    product: getProductName(productCode),
    quote,
    remaining: qtyFormatter(remainingQty),
    time: hourFormat(order.last_updated_at),
    qty: qtyFormatter(order.quantity),
    price: formattedPrice,
    side: orderSide,
  });

  return (
    <Row
      columns={columns}
      dataTestId={`urfq-open-orders-row-${order.id}`}
      onContextMenu={({ x, y }) => {
        open<RfqContextMenuData>({
          rfqId: order.rfq_id,
          account: order.account_name,
          anchorPoint: { x, y },
          productCode,
          data: { order, auctionType: 'RFQ' },
        });
      }}
    />
  );
}

function GrfqOrderRow({ quote }: { quote: GrfqQuote }) {
  const productCode = quote.product_codes?.[0];
  const qtyFormatter = (value: string | number) =>
    formatProductQty(value, productCode);
  const formattedPrice = useFormattedGrfqQuotePrice(quote);
  const { open } = useContextMenuActions();

  const actions = (
    // WIP: PTP-18877: Include account from trade object when available
    <ActionsContainer>
      <QuickCreateButton
        baseDDTag={baseDDTag}
        type="RFQ"
        rfqId={quote.rfq_id}
      />
      <KillQuoteButton quoteId={quote.id} />
    </ActionsContainer>
  );

  const quoteSide = <ActionLabel>{quote.side}</ActionLabel>;

  const quoteContainer = (
    <QuoteContainer>
      <QuoteRow>
        <QuoteCell>{qtyFormatter(quote.original_quantity)}</QuoteCell>
        <QuoteCell>{formattedPrice}</QuoteCell>
        {quoteSide}
      </QuoteRow>
    </QuoteContainer>
  );

  const description = (
    <StrategyDescription>{quote.description}</StrategyDescription>
  );

  const columns = makeColumns({
    account: quote.account,
    actions,
    auction: 'OB',
    completed: qtyFormatter(quote.completed_quantity),
    date: shortDateFormat(quote.last_activity),
    description,
    oid: quote.id,
    product: getProductName(productCode),
    quote: quoteContainer,
    remaining: qtyFormatter(quote.remaining_quantity),
    time: hourFormat(quote.last_activity),
    qty: qtyFormatter(quote.original_quantity),
    price: formattedPrice,
    side: quoteSide,
  });

  return (
    <Row
      dataTestId={`urfq-open-orders-row-${quote.id}`}
      columns={columns}
      onContextMenu={({ x, y }) => {
        open<ObContextMenuData>({
          rfqId: quote.rfq_id,
          anchorPoint: { x, y },
          data: { quote, auctionType: 'OB' },
        });
      }}
    />
  );
}

export function OrderHeaderRow() {
  const columns = makeColumns()
    .filter((col) => col.desktopOptions?.isHidden !== true)
    .map((col) => {
      if (col.id === 'quote') {
        return (
          <QuoteRow key={col.id}>
            <QuoteCell>Qty</QuoteCell>
            <QuoteCell>Price</QuoteCell>
            <QuoteCell>Side</QuoteCell>
          </QuoteRow>
        );
      }
      return <span key={col.id}>{col.name}</span>;
    });

  return <HeaderRow>{columns}</HeaderRow>;
}

const QuoteRow = styled.div`
  display: grid;
  width: 248px;
  justify-content: center;
  grid-template-columns: 70px 94px 52px;
  gap: 16px;
`;

const QuoteContainer = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  border-radius: 3px;
  height: 30px;
  background-color: #0f2038;
`;

const COLUMNS_CSS = css`
  grid-template-columns: 68px 50px 50px 120px 250px 185px 248px 70px 70px 220px 200px;
  grid-template-areas: 'date time auction prod desc actions quote completed remaining oid account';
  gap: 1rem;
`;

const Row = styled(BottomTableRow)`
  ${COLUMNS_CSS}

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    .mobile-bottom-table-row {
      display: grid;
      grid-template-columns: 30px 25px auto;
    }
  }
`;

const HeaderRow = styled(DesktopRow)`
  ${COLUMNS_CSS}
  position: sticky;
  top: 0;
  z-index: 1;
  color: ${paradigmLightBlue};

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const QuoteCell = styled.span`
  text-align: center;
`;

const ActionLabel = styled.span`
  text-align: center;
  font-size: 14px;
  ${(props) => props.children === 'SELL' && `color: ${razzmatazz};`}
  ${(props) => props.children === 'BUY' && `color: ${lima};`}
`;
